import { useId } from 'react';
import { Rocket, Sparkles, X } from 'lucide-react';
import { cn } from '../../utils/cn';
import { useCountdown } from '../../hooks/useCountdown';
import { formatCountdown, formatReleaseAt, localZoneName, releaseInputValue } from '../../utils/comingSoon';
import type { MaintenanceMode } from '../../services/maintenanceApi';
import './comingSoon.css';

/**
 * The pieces every page uses to draw a "Coming soon" window.
 *
 * Coming soon is maintenance underneath, but it is news rather than a problem:
 * a new pack that is shown before it opens. So it borrows maintenance's
 * structure (a badge, an overlay, the same control) and none of its warning
 * colours. Violet running into pink, a sparkle, and a countdown when staff
 * have named a time. It is never drawn in the red or amber of an outage.
 */

/** "Opens in 2d 04h 13m 09s", ticking; "Opening any moment" once the time has passed. */
export function Countdown({ releaseAt, className, prefix = 'Opens in' }: {
  releaseAt: number | null;
  className?: string;
  prefix?: string;
}) {
  const { parts, still } = useCountdown(releaseAt);
  if (releaseAt === null || !parts) return null;
  const at = formatReleaseAt(releaseAt);
  if (parts.passed) {
    return (
      <span className={cn('coming-soon-countdown coming-soon-countdown-due', className)} title={`Was due ${at}`}>
        Opening any moment
      </span>
    );
  }
  // role="timer" is not announced as it changes, which is right: a screen
  // reader should not read out every second. The label carries the moment.
  return (
    <span className={cn('coming-soon-countdown', className)} role="timer" aria-label={`Opens ${at}`} title={`Opens ${at}`}>
      {prefix && <>{prefix} </>}<b>{formatCountdown(parts, { seconds: !still })}</b>
    </span>
  );
}

/** The small pill: "✦ Coming soon". */
export function ComingSoonBadge({ className, label = 'Coming soon' }: { className?: string; label?: string }) {
  return (
    <span className={cn('coming-soon-badge', className)}>
      <Sparkles className="coming-soon-badge-icon" aria-hidden />
      {label}
    </span>
  );
}

/**
 * The headline block, for a card or panel whose servers are all coming soon.
 *
 * `releaseAt` is the time they share: a number, null when none was given, or
 * undefined when the servers disagree, in which case each server's own line
 * carries its time and the banner says so rather than choosing one.
 */
export function ComingSoonBanner({ releaseAt, scope, className }: {
  releaseAt: number | null | undefined;
  /** e.g. "1 of 3 servers", for a partial cluster. Omitted when it is all of them. */
  scope?: string;
  className?: string;
}) {
  return (
    <div className={cn('coming-soon-banner', className)}>
      <Rocket className="coming-soon-banner-icon" aria-hidden />
      <div className="min-w-0">
        <p className="coming-soon-banner-title">
          Coming soon{scope && <span className="coming-soon-banner-scope"> · {scope}</span>}
        </p>
        <p className="coming-soon-banner-line">New server — not open to the public yet</p>
        {releaseAt === undefined
          ? <p className="coming-soon-banner-line">Release times differ by server</p>
          : releaseAt === null
            ? <p className="coming-soon-banner-line">No release time announced</p>
            : <p className="coming-soon-banner-line">
                Opens {formatReleaseAt(releaseAt)} · <Countdown releaseAt={releaseAt} prefix="" className="coming-soon-banner-countdown" />
              </p>}
      </div>
    </div>
  );
}

/**
 * The choice a start makes: which kind of window, and for coming soon an
 * optional release time. The time is typed in the viewer's own zone, which is
 * named beside the field, and converted to unix seconds by the caller
 * (parseReleaseInput) so the bot and every other viewer get the same instant.
 */
export function WindowModeFields({ mode, onMode, release, onRelease, error, disabled, className }: {
  mode: MaintenanceMode;
  onMode: (mode: MaintenanceMode) => void;
  /** The raw datetime-local value. */
  release: string;
  onRelease: (value: string) => void;
  error?: string | null;
  disabled?: boolean;
  className?: string;
}) {
  const id = useId();
  // The picker's floor: the next whole minute. The field still validates on
  // its own, since the floor is only a hint to the browser's picker.
  const min = releaseInputValue(Math.ceil(Date.now() / 60_000) * 60);
  return (
    <div className={cn('window-mode-fields', className)}>
      <div className="window-mode-choice" role="radiogroup" aria-label="Kind of window">
        {([['maintenance', 'Maintenance'], ['coming_soon', 'Coming soon']] as const).map(([value, label]) => (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={mode === value}
            data-active={mode === value}
            className={cn('window-mode-option', value === 'coming_soon' && 'window-mode-option-soon')}
            disabled={disabled}
            onClick={() => onMode(value)}
          >
            {value === 'coming_soon' && <Sparkles className="w-3 h-3" aria-hidden />}
            {label}
          </button>
        ))}
      </div>
      {mode === 'coming_soon' && (
        <div className="window-mode-release">
          <label htmlFor={`${id}-release`}>
            Opens <span className="window-mode-optional">(optional)</span>
          </label>
          <div className="window-mode-release-input">
            <input
              id={`${id}-release`}
              type="datetime-local"
              className="glass-input"
              value={release}
              min={min}
              disabled={disabled}
              aria-invalid={Boolean(error)}
              aria-describedby={`${id}-release-hint`}
              onChange={event => onRelease(event.target.value)}
            />
            {release && (
              <button type="button" className="window-mode-clear" disabled={disabled} onClick={() => onRelease('')} aria-label="Clear the release time" title="Clear the release time">
                <X className="w-3 h-3" aria-hidden />
              </button>
            )}
          </div>
          <p id={`${id}-release-hint`} className={cn('window-mode-hint', error && 'window-mode-error')} role={error ? 'alert' : undefined}>
            {error || `In ${localZoneName()}. Leave empty for no countdown; the server opens when the window is ended.`}
          </p>
        </div>
      )}
    </div>
  );
}
